import type { EvaluationPanelMember } from "../../../types/panel.type";

/**
 * Illustrative rows shown ONLY while the "Show Me" tour is running and the panel is
 * genuinely empty, so the Panel Members step has something to point at instead of the
 * "No panel members added yet" placeholder.
 *
 * SAFETY — these are display-only and must never reach the API:
 *  - Negative ids: they cannot collide with real member ids, so if one ever leaked into
 *    a payload it would fail loudly server-side rather than mutating a real member.
 *  - The step renders them WITHOUT `onToggleSelect`, and PanelMemberTable derives
 *    `showSelection = canManage && pendingMembers.length > 0 && !!onToggleSelect`, so no
 *    checkboxes render, nothing can enter `selectedInviteIds`, and "Send Invitations"
 *    stays disabled at 0.
 *  - They are swapped out in `onAfterShowMe`, so they cannot outlive the tour.
 *  - Names are obviously illustrative, so nobody mistakes them for real invitees.
 */
export const TOUR_PREVIEW_MEMBERS: EvaluationPanelMember[] = [
	{
		// Added to the panel but not yet invited — the state a lead starts in.
		id: -101,
		name: "Example — Not Yet Invited",
		accepted_at: null,
		email: "not.invited@example.com",
		invited_at: null,
		member_type: "internal",
		panel_role: "member",
		rfx_id: 0,
		status: "pending",
		user_id: null,
		uuid: "tour-preview-pending",
		disclaimer_status: null,          // disclaimer not yet completed
	},
	{
		// Invitation email sent, awaiting the member.
		id: -102,
		name: "Example — Invited",
		accepted_at: null,
		email: "invited@example.com",
		invited_at: new Date(0).toISOString(),
		member_type: "external",
		panel_role: "member",
		rfx_id: 0,
		status: "invited",
		user_id: null,
		uuid: "tour-preview-invited",
		disclaimer_status: "no_conflict", // completed, nothing declared
	},
	{
		// Accepted, and declared a conflict — the row a lead must action.
		id: -103,
		name: "Example — Declared a Conflict",
		accepted_at: new Date(0).toISOString(),
		email: "conflict@example.com",
		invited_at: new Date(0).toISOString(),
		member_type: "internal",
		panel_role: "member",
		rfx_id: 0,
		status: "accepted",
		user_id: null,
		uuid: "tour-preview-conflict",
		disclaimer_status: "conflict",
		disclaimer_conflict_details: "Example: previously engaged by one of the suppliers.",
		// status "conflict" => secondary badge "Pending": declared, awaiting the
		// lead's review. Distinct from "reviewed" below, which carries an outcome.
	},
	{
		// Conflict declared AND reviewed — the resolved end state.
		id: -104,
		name: "Example — Conflict Reviewed",
		accepted_at: new Date(0).toISOString(),
		email: "reviewed@example.com",
		invited_at: new Date(0).toISOString(),
		member_type: "internal",
		panel_role: "member",
		rfx_id: 0,
		status: "accepted",
		user_id: null,
		uuid: "tour-preview-reviewed",
		disclaimer_status: "reviewed",
		disclaimer_review_action: "accepted",   // => secondary badge "Reviewed"
		disclaimer_conflict_details: "Example: minor prior contact, reviewed and accepted.",
	},
];

/** Guards against a preview row ever being treated as real. */
export const isTourPreviewMember = (id: number): boolean => id < 0;
